import { FC, useState, useMemo, useEffect } from 'react';
import {
  Grid,
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import {
  Assessment,
  TrendingUp,
  CheckCircle,
  Schedule,
  FilterList,
  ExpandMore,
  Close,
  Add,
  ArrowBack,
  Edit,
} from '@mui/icons-material';
import { useAcademicsAssessmentsContext } from '../../contexts/academicsAssessments.context';
import AcademicsAssessmentsGrid from './academicsAssessmentsGrid.view';
import AcademicsAssessmentDetailsView, {
  EAssessmentDetailsStep,
} from './academicsAssessmentDetails.view';
import EditSubjectScoresView from './editSubjectScores.view';
import AddEditAssessmentView from './addEditAssessment.view';
import {
  EAssessmentType,
  EAssessmentStatus,
  IAssessmentSubject,
} from '../../../../../apis/academics/academicsAssessments.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useAcademicsClassesAndCoursesState } from '../../contexts/academicsClassesAndCourses.context';

enum EAssessmentView {
  GRID = 'grid',
  DETAILS = 'details',
}


interface FilterState {
  types: Set<EAssessmentType>;
  statuses: Set<EAssessmentStatus>;
  selectedSectionId?: string;
}

const AcademicsAssessments: FC = () => {
  const { translateText } = useTranslation();
  const theme = useTheme();
  const {
    getAssessmentsByClass,
    isLoading,
    selectedAssessmentId,
    clearSelectedAssessment,
    refetchClasses,
    assessmentDetailsQuery,
  } = useAcademicsAssessmentsContext();
  const { academicsClassesAndCoursesAppState } = useAcademicsClassesAndCoursesState();

  const [selectedSectionId, setSelectedSectionId] = useState<string | undefined>(undefined);
  const [filters, setFilters] = useState<FilterState>({
    types: new Set(),
    statuses: new Set(),
  });
  const [currentView, setCurrentView] = useState<EAssessmentView>(
    EAssessmentView.GRID
  );
  const [assessmentDetailsStep, setAssessmentDetailsStep] = useState<EAssessmentDetailsStep>(
    EAssessmentDetailsStep.SUBJECTS
  );
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState<string | null>(null);
  const [isEditSubjectScoresOpen, setIsEditSubjectScoresOpen] = useState(false);
  const [selectedSubjectForEdit, setSelectedSubjectForEdit] = useState<IAssessmentSubject | null>(null);

  // Section options derived from classes data
  const sectionOptions = useMemo(() => {
    const classes = academicsClassesAndCoursesAppState.classes;
    const sectionMap = new Map<string, string>();
    classes.forEach((cls) => {
      if (cls.sections?.length) {
        cls.sections.forEach((s) => sectionMap.set(s.id, s.name));
      } else if (cls.section) {
        sectionMap.set(cls.section, cls.section);
      }
    });
    return Array.from(sectionMap.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, name]) => ({ key: id, value: `Section ${name}` }));
  }, [academicsClassesAndCoursesAppState.classes]);

  // Derive class ID from selected section for the AddEdit dialog
  const selectedClassIdFromSection = useMemo(() => {
    if (!selectedSectionId) return undefined;
    const cls = academicsClassesAndCoursesAppState.classes.find((c) =>
      c.sections?.some((s) => s.id === selectedSectionId) || c.section === selectedSectionId
    );
    return cls?.id;
  }, [selectedSectionId, academicsClassesAndCoursesAppState.classes]);

  const classOptionsForDialog = useMemo(() => {
    return academicsClassesAndCoursesAppState.classes.map((cls) => ({
      key: cls.id,
      value: cls.name,
    }));
  }, [academicsClassesAndCoursesAppState.classes]);

  const selectedItemData = useMemo(() => {
    if (!selectedSectionId) return null;

    // Find classes that belong to the selected section
    const matchingClasses = academicsClassesAndCoursesAppState.classes.filter((cls) =>
      cls.sections?.some((s) => s.id === selectedSectionId) || cls.section === selectedSectionId
    );

    if (matchingClasses.length === 0) return null;

    // Get assessments for the first matching class
    const classId = matchingClasses[0].id;
    return getAssessmentsByClass(classId);
  }, [selectedSectionId, academicsClassesAndCoursesAppState.classes, getAssessmentsByClass]);

  const stats = useMemo(() => {
    if (!selectedItemData) {
      return {
        totalAssessments: 0,
        averageScore: 0,
        averagePercentage: 0,
        completionRate: 0,
        scheduledCount: 0,
        completedCount: 0,
        inProgressCount: 0,
        assessmentsByType: {} as Record<EAssessmentType, number>,
      };
    }

    const allAssessments = Object.values(selectedItemData.assessments).flat();
    const scheduledCount = allAssessments.filter(
      (a) => a.status === EAssessmentStatus.SCHEDULED
    ).length;
    const completedCount = allAssessments.filter(
      (a) =>
        a.status === EAssessmentStatus.COMPLETED ||
        a.status === EAssessmentStatus.GRADED
    ).length;
    const inProgressCount = allAssessments.filter(
      (a) => a.status === EAssessmentStatus.IN_PROGRESS
    ).length;

    const assessmentsByType = Object.entries(
      selectedItemData.assessments
    ).reduce(
      (acc, [type, assessments]) => {
        acc[type as EAssessmentType] = assessments?.length || 0;
        return acc;
      },
      {} as Record<EAssessmentType, number>
    );

    return {
      totalAssessments: selectedItemData.totalAssessments,
      averageScore: selectedItemData.averageScore,
      averagePercentage: selectedItemData.averagePercentage,
      completionRate: selectedItemData.completionRate,
      scheduledCount,
      completedCount,
      inProgressCount,
      assessmentsByType,
    };
  }, [selectedItemData]);

  const toggleFilter = (
    filterType: 'types' | 'statuses',
    value: EAssessmentType | EAssessmentStatus
  ) => {
    setFilters((prev) => {
      if (filterType === 'types') {
        const currentSet = new Set(prev.types);
        const typedValue = value as EAssessmentType;
        if (currentSet.has(typedValue)) {
          currentSet.delete(typedValue);
        } else {
          currentSet.add(typedValue);
        }
        return {
          ...prev,
          types: currentSet,
        };
      } else {
        const currentSet = new Set(prev.statuses);
        const typedValue = value as EAssessmentStatus;
        if (currentSet.has(typedValue)) {
          currentSet.delete(typedValue);
        } else {
          currentSet.add(typedValue);
        }
        return {
          ...prev,
          statuses: currentSet,
        };
      }
    });
  };

  const clearFilters = (filterType?: 'types' | 'statuses') => {
    if (filterType) {
      setFilters((prev) => ({
        ...prev,
        [filterType]: new Set(),
      }));
    } else {
      setFilters({
        types: new Set(),
        statuses: new Set(),
        selectedSectionId: undefined,
      });
    }
  };

  const hasActiveFilters = () => {
    return filters.types.size > 0 || filters.statuses.size > 0;
  };

  const handleSectionSelect = (sectionId: string) => {
    setSelectedSectionId(sectionId);
    setFilters((prev) => ({
      ...prev,
      selectedSectionId: sectionId,
    }));
  };

  const handleBackToGrid = () => {
    setCurrentView(EAssessmentView.GRID);
    setAssessmentDetailsStep(EAssessmentDetailsStep.SUBJECTS);
    clearSelectedAssessment();
  };

  // Sync view with selected assessment
  useEffect(() => {
    if (selectedAssessmentId) {
      setCurrentView(EAssessmentView.DETAILS);
      setAssessmentDetailsStep(EAssessmentDetailsStep.SUBJECTS);
    } else if (
      currentView === EAssessmentView.DETAILS &&
      !selectedAssessmentId
    ) {
      setCurrentView(EAssessmentView.GRID);
      setSelectedSubjectForEdit(null);
    }
  }, [selectedAssessmentId, currentView, clearSelectedAssessment]);

  // Reset selected subject when step changes away from SCORES
  useEffect(() => {
    if (assessmentDetailsStep !== EAssessmentDetailsStep.SCORES) {
      setSelectedSubjectForEdit(null);
    }
  }, [assessmentDetailsStep]);

  return (
    <Grid
      container
      spacing={{ xs: 1, sm: 2 }}
      sx={{ height: '100%', p: { xs: 1, sm: 2 } }}
      data-testid="academics-assessments-view"
    >
      {/* Selection Section */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 2, sm: 0 },
            mb: { xs: 1, sm: 2 },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 }, alignItems: 'stretch', flex: 1 }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel>Select Section</InputLabel>
              <Select
                value={selectedSectionId || ''}
                label="Select Section"
                onChange={(e) => handleSectionSelect(e.target.value as string)}
                sx={{
                  minHeight: { xs: '44px', sm: '40px' },
                  fontSize: { xs: '16px', sm: '14px' },
                }}
                data-testid="select-section-dropdown"
              >
                {sectionOptions.map((option) => (
                  <MenuItem key={option.key} value={option.key} sx={{ minHeight: { xs: '44px', sm: '36px' } }}>
                    {option.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {selectedSectionId && (
            <>
              {currentView === EAssessmentView.DETAILS ? (
                assessmentDetailsStep === EAssessmentDetailsStep.SCORES ? (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Edit />}
                    onClick={() => {
                      if (selectedSubjectForEdit && selectedAssessmentId) {
                        setIsEditSubjectScoresOpen(true);
                      }
                    }}
                    disabled={!selectedSubjectForEdit}
                    sx={{
                      minHeight: { xs: '44px', sm: '36px' },
                      fontSize: { xs: '16px', sm: '14px' },
                      width: { xs: '100%', sm: 'auto' },
                      ml: { xs: 0, sm: 2 },
                    }}
                    data-testid="edit-subject-scores-button-header"
                  >
                    {translateText('academics.assessments.editSubjectScores')}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Edit />}
                    onClick={() => {
                      if (selectedAssessmentId) {
                        setEditingAssessment(selectedAssessmentId);
                        setIsAddDialogOpen(true);
                      }
                    }}
                    sx={{
                      minHeight: { xs: '44px', sm: '36px' },
                      fontSize: { xs: '16px', sm: '14px' },
                      width: { xs: '100%', sm: 'auto' },
                      ml: { xs: 0, sm: 2 },
                    }}
                    data-testid="edit-assessment-button-header"
                  >
                    {translateText('academics.assessments.editAssessment')}
                  </Button>
                )
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onClick={() => {
                    setIsAddDialogOpen(true);
                  }}
                  sx={{
                    minHeight: { xs: '44px', sm: '36px' },
                    fontSize: { xs: '16px', sm: '14px' },
                    width: { xs: '100%', sm: 'auto' },
                    ml: { xs: 0, sm: 2 },
                  }}
                  data-testid="add-assessment-button"
                >
                  {translateText('academics.assessments.addAssessment')}
                </Button>
              )}
            </>
          )}
        </Box>
      </Grid>

      {/* Stats Section */}
      {selectedItemData && selectedSectionId && currentView === EAssessmentView.GRID && (
        <Grid item xs={12}>
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.light}05 100%)`,
                  border: `1px solid ${theme.palette.primary.light}40`,
                  borderRadius: 2,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${theme.palette.primary.light}30`,
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Typography
                        color="textSecondary"
                        gutterBottom
                        variant="body2"
                        fontWeight={500}
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {translateText(
                          'academics.assessments.totalAssessments'
                        )}
                      </Typography>
                      <Typography
                        variant="h5"
                        component="div"
                        fontWeight={600}
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.primary.main }}
                      >
                        {stats.totalAssessments}
                      </Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: theme.palette.primary.dark }}>
                        {stats.completedCount + stats.inProgressCount}{' '}
                        {translateText('academics.assessments.active') || 'Active'}
                      </Typography>
                    </Box>
                    <Assessment sx={{ fontSize: { xs: '2rem', sm: '2.5rem' }, color: theme.palette.primary.main }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.success.light}15 0%, ${theme.palette.success.light}05 100%)`,
                  border: `1px solid ${theme.palette.success.light}40`,
                  borderRadius: 2,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${theme.palette.success.light}30`,
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Typography
                        color="textSecondary"
                        gutterBottom
                        variant="body2"
                        fontWeight={500}
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {translateText('academics.assessments.averageScore')}
                      </Typography>
                      <Typography
                        variant="h5"
                        component="div"
                        fontWeight={600}
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.success.main }}
                      >
                        {stats.averageScore.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: theme.palette.success.dark }}>
                        {stats.averagePercentage.toFixed(2)}%
                      </Typography>
                    </Box>
                    <TrendingUp sx={{ fontSize: { xs: '2rem', sm: '2.5rem' }, color: theme.palette.success.main }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.info.light}15 0%, ${theme.palette.info.light}05 100%)`,
                  border: `1px solid ${theme.palette.info.light}40`,
                  borderRadius: 2,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${theme.palette.info.light}30`,
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Typography
                        color="textSecondary"
                        gutterBottom
                        variant="body2"
                        fontWeight={500}
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {translateText('academics.assessments.completionRate')}
                      </Typography>
                      <Typography
                        variant="h5"
                        component="div"
                        fontWeight={600}
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.info.main }}
                      >
                        {stats.completionRate.toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: theme.palette.info.dark }}>
                        {stats.completedCount}{' '}
                        {translateText('academics.assessments.completed')}
                      </Typography>
                    </Box>
                    <CheckCircle sx={{ fontSize: { xs: '2rem', sm: '2.5rem' }, color: theme.palette.info.main }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.warning.light}15 0%, ${theme.palette.warning.light}05 100%)`,
                  border: `1px solid ${theme.palette.warning.light}40`,
                  borderRadius: 2,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${theme.palette.warning.light}30`,
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Typography
                        color="textSecondary"
                        gutterBottom
                        variant="body2"
                        fontWeight={500}
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {translateText('academics.assessments.scheduled')}
                      </Typography>
                      <Typography
                        variant="h5"
                        component="div"
                        fontWeight={600}
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.warning.main }}
                      >
                        {stats.scheduledCount}
                      </Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: theme.palette.warning.dark }}>
                        {stats.inProgressCount}{' '}
                        {translateText('academics.assessments.inProgress')}
                      </Typography>
                    </Box>
                    <Schedule sx={{ fontSize: { xs: '2rem', sm: '2.5rem' }, color: theme.palette.warning.main }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      )}

      {/* Filters Section */}
      {selectedItemData && selectedSectionId && currentView === EAssessmentView.GRID && (
        <Grid item xs={12}>
          <Accordion
            defaultExpanded={hasActiveFilters()}
            sx={{
              '&:before': {
                display: 'none',
              },
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  margin: '8px 0',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                }}
              >
                <FilterList color="action" />
                <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                  {translateText('academics.assessments.filters')}
                </Typography>
                {hasActiveFilters() && (
                  <>
                    <Badge
                      badgeContent={filters.types.size + filters.statuses.size}
                      color="primary"
                      sx={{ ml: 1 }}
                    >
                      <Chip
                        size="medium"
                        label={translateText(
                          'academics.assessments.activeFilters'
                        )}
                        color="primary"
                        variant="outlined"
                      />
                    </Badge>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<Close />}
                      onClick={(e) => {
                        e.stopPropagation();
                        clearFilters();
                      }}
                      sx={{ ml: 2 }}
                    >
                      {translateText('academics.assessments.clearAll')}
                    </Button>
                  </>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Grid container spacing={{ xs: 1, sm: 2 }}>
                <Grid item xs={12}>
                  <Box sx={{ mb: { xs: 1, sm: 2 } }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {translateText('academics.assessments.assessmentTypes')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
                      {Object.values(EAssessmentType).map((type) => (
                        <Chip
                          key={type}
                          label={`${type} (${stats.assessmentsByType[type] || 0})`}
                          color={
                            filters.types.has(type) ? 'primary' : 'default'
                          }
                          variant={
                            filters.types.has(type) ? 'filled' : 'outlined'
                          }
                          onClick={() => toggleFilter('types', type)}
                          sx={{
                            cursor: 'pointer',
                            minHeight: { xs: '32px', sm: '32px' },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            '&:hover': {
                              backgroundColor: filters.types.has(type)
                                ? 'primary.main'
                                : 'action.hover',
                            },
                          }}
                          data-testid={`filter-type-${type}`}
                        />
                      ))}
                      {filters.types.size > 0 && (
                        <Chip
                          label={translateText(
                            'academics.assessments.clearTypes'
                          )}
                          color="error"
                          variant="outlined"
                          onClick={() => clearFilters('types')}
                          sx={{ cursor: 'pointer', minHeight: { xs: '32px', sm: '32px' } }}
                          data-testid="clear-types-button"
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: { xs: 1, sm: 2 } }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {translateText('academics.assessments.statusLabel')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
                      {Object.values(EAssessmentStatus).map((status) => (
                        <Chip
                          key={status}
                          label={status}
                          color={
                            filters.statuses.has(status) ? 'primary' : 'default'
                          }
                          variant={
                            filters.statuses.has(status) ? 'filled' : 'outlined'
                          }
                          onClick={() => toggleFilter('statuses', status)}
                          sx={{
                            cursor: 'pointer',
                            minHeight: { xs: '32px', sm: '32px' },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            '&:hover': {
                              backgroundColor: filters.statuses.has(status)
                                ? 'primary.main'
                                : 'action.hover',
                            },
                          }}
                          data-testid={`filter-status-${status}`}
                        />
                      ))}
                      {filters.statuses.size > 0 && (
                        <Chip
                          label={translateText(
                            'academics.assessments.clearStatus'
                          )}
                          color="error"
                          variant="outlined"
                          onClick={() => clearFilters('statuses')}
                          sx={{ cursor: 'pointer', minHeight: { xs: '32px', sm: '32px' } }}
                          data-testid="clear-status-button"
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}

      {/* Grid or Details Section */}
      <Grid item xs={12}>
        {currentView === EAssessmentView.DETAILS ? (
          <Box>
            {assessmentDetailsStep !== EAssessmentDetailsStep.SCORES && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: { xs: 1, sm: 2 },
                }}
                data-testid="assessment-details-header"
              >
                <Button
                  startIcon={<ArrowBack />}
                  onClick={handleBackToGrid}
                  sx={{
                    mr: { xs: 1, sm: 2 },
                    minHeight: { xs: '44px', sm: '36px' },
                    fontSize: { xs: '16px', sm: '14px' },
                  }}
                  data-testid="back-to-assessments-button"
                >
                  {translateText('academics.assessments.backToAssessments')}
                </Button>
              </Box>
            )}
            <AcademicsAssessmentDetailsView
              onStepChange={(step) => setAssessmentDetailsStep(step)}
              onEditAssessment={() => {
                if (selectedAssessmentId) {
                  setEditingAssessment(selectedAssessmentId);
                  setIsAddDialogOpen(true);
                }
              }}
              onSelectedSubjectChange={(subject) => {
                setSelectedSubjectForEdit(subject);
              }}
            />
          </Box>
        ) : (
          <AcademicsAssessmentsGrid
            selectedItem={selectedItemData || null}
            filters={filters}
            isLoading={isLoading}
          />
        )}
      </Grid>
      {/* Add/Edit Assessment Dialog */}
      <AddEditAssessmentView
        open={isAddDialogOpen}
        onClose={() => {
          setIsAddDialogOpen(false);
          setEditingAssessment(null);
        }}
        onSuccess={() => {
          setIsAddDialogOpen(false);
          setEditingAssessment(null);
          refetchClasses();
          // Refetch assessment details if viewing an assessment
          if (selectedAssessmentId) {
            // The context will automatically refetch when selectedAssessmentId changes
          }
        }}
        assessmentId={editingAssessment}
        preSelectedClassId={selectedClassIdFromSection}
        classOptions={classOptionsForDialog}
      />
      {/* Edit Subject Scores Dialog */}
      {selectedSubjectForEdit && selectedAssessmentId && (
        <EditSubjectScoresView
          open={isEditSubjectScoresOpen}
          onClose={() => {
            setIsEditSubjectScoresOpen(false);
            // Don't clear selectedSubjectForEdit on cancel - keep it so button remains enabled
          }}
          onSuccess={() => {
            setIsEditSubjectScoresOpen(false);
            // Keep selectedSubjectForEdit so button remains enabled after successful save
            refetchClasses();
            assessmentDetailsQuery?.refetch();
          }}
          assessmentId={selectedAssessmentId}
          subject={selectedSubjectForEdit}
        />
      )}
    </Grid>
  );
};

export default AcademicsAssessments;
